class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.tiers = JSON.parse(this.dataset.tiers);
    this.tier = this.tiers[1] || this.tiers[0];
    this.items = new Map(); // variantId -> {qty, price, img, title}
    this.sum = this.querySelector('[data-summary]');
    this.querySelectorAll('[data-size]').forEach((b) => b.addEventListener('click', () => {
      this.tier = this.tiers.find((t) => t.size === Number(b.dataset.size));
      this.querySelectorAll('[data-size]').forEach((x) => x.setAttribute('aria-checked', x.dataset.size === b.dataset.size));
      this.trim();
      this.render();
    }));
    this.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-remove]');
      if (rm) { this.change(this.liById(rm.dataset.remove), -1); return; }
      const li = e.target.closest('[data-product]');
      if (!li) return;
      if (e.target.closest('[data-inc]')) this.change(li, 1, li.querySelector('.bb__media'));
      if (e.target.closest('[data-dec]')) this.change(li, -1);
    });
    this.initDrag();
    this.querySelector('[data-toggle]').addEventListener('click', (e) => {
      const open = this.sum.classList.toggle('is-open');
      e.currentTarget.setAttribute('aria-expanded', open);
    });
    this.querySelector('[data-add]').addEventListener('click', () => this.add('cart'));
    this.querySelector('[data-checkout]').addEventListener('click', () => this.add('checkout'));
    addEventListener('pageshow', (e) => e.persisted && this.render());
    this.prevN = 0;
    // sticky offsets follow the real (sticky) header height
    const hd = document.querySelector('.hd-wrap');
    const setTop = () => this.style.setProperty('--bb-top', `${hd ? Math.round(hd.getBoundingClientRect().height) : 0}px`);
    setTop();
    if (hd && 'ResizeObserver' in window) new ResizeObserver(setTop).observe(hd);
    addEventListener('resize', setTop);
    this.render();
    this.reveal();
  }

  reveal() {
    const els = this.querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.classList.add('is-ready');
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  }

  count() { let n = 0; this.items.forEach((i) => (n += i.qty)); return n; }

  liById(id) { return this.querySelector(`[data-product][data-id="${id}"]`); }

  // d > 0 adds; `from` (element) animates a copy of its image flying into the next slot
  change(li, d, from) {
    const id = li.dataset.id;
    const cur = this.items.get(id) || { id, qty: 0, price: Number(li.dataset.price), img: li.dataset.img, title: li.dataset.title };
    if (d > 0 && this.count() >= this.tier.size) { this.shake(); return false; }
    const before = Math.max(0, Math.min(Number(li.dataset.max) || 99, cur.qty + d));
    if (before === cur.qty) return false;
    const rect = d > 0 && from ? from.getBoundingClientRect() : null;
    const imgSrc = li.querySelector('.bb__media img');
    cur.qty = before;
    cur.qty ? this.items.set(id, cur) : this.items.delete(id);
    this.render();
    if (rect && imgSrc) this.fly(imgSrc.currentSrc || imgSrc.src, rect);
    return true;
  }

  shake() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.sum.classList.remove('is-shake'); void this.sum.offsetWidth; this.sum.classList.add('is-shake');
  }

  reduced() { return matchMedia('(prefers-reduced-motion: reduce)').matches; }

  // copy of the product image flies from `rect` into the newest slot (or the counter when the bar is collapsed)
  fly(src, rect, ghost) {
    const slot = this.querySelector('.bb__slot.is-landing');
    let tr = slot && slot.getBoundingClientRect();
    if (!tr || !tr.width) tr = this.querySelector('[data-count]').getBoundingClientRect();
    const land = () => { if (slot) { slot.classList.remove('is-landing'); slot.classList.add('is-new'); } };
    if (this.reduced() || !tr || !tr.width) { land(); ghost && ghost.remove(); return; }
    const g = ghost || document.createElement('img');
    if (!ghost) { g.src = src; g.alt = ''; g.className = 'bb__ghost'; document.body.append(g); }
    const w = g.offsetWidth || rect.width, h = g.offsetHeight || rect.height;
    const from = ghost ? g.getBoundingClientRect() : rect;
    const dx = tr.left + tr.width / 2 - (from.left + from.width / 2);
    const dy = tr.top + tr.height / 2 - (from.top + from.height / 2);
    const sc = Math.min(tr.width / from.width, 1) || .4;
    g.style.cssText = `left:${from.left}px;top:${from.top}px;width:${from.width}px;height:${from.height}px;transform:none`;
    const a = g.animate([
      { transform: 'translate(0,0) scale(1)', opacity: 1 },
      { transform: `translate(${dx * .6}px,${dy * .6 - 40}px) scale(${(1 + sc) / 2})`, opacity: 1, offset: .6 },
      { transform: `translate(${dx}px,${dy}px) scale(${sc})`, opacity: .9 }
    ], { duration: 560, easing: 'cubic-bezier(.2,.7,.2,1)' });
    a.onfinish = a.oncancel = () => { g.remove(); land(); };
  }

  // desktop mouse/pen: drag a product image onto the bundle panel. Touch keeps native scroll + the + button.
  initDrag() {
    let st = null;
    const clear = () => { document.body.classList.remove('bb-dragging'); this.sum.classList.remove('is-drop'); st = null; };
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      const media = e.target.closest('.bb__media');
      const li = media && media.closest('[data-product]');
      if (!li || li.classList.contains('is-soldout') || !li.querySelector('[data-inc]')) return;
      st = { li, media, x: e.clientX, y: e.clientY, on: false, ghost: null, id: e.pointerId };
    });
    window.addEventListener('pointermove', (e) => {
      if (!st || e.pointerId !== st.id) return;
      if (!st.on) {
        if (Math.hypot(e.clientX - st.x, e.clientY - st.y) < 8) return;
        const img = st.media.querySelector('img');
        if (!img) { st = null; return; }
        const r = img.getBoundingClientRect();
        const g = document.createElement('img');
        g.src = img.currentSrc || img.src; g.alt = ''; g.className = 'bb__ghost is-drag';
        g.style.cssText = `left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px`;
        document.body.append(g);
        st.ghost = g; st.rect = r; st.on = true;
        st.li.classList.add('is-dragging');
        document.body.classList.add('bb-dragging');
      }
      e.preventDefault();
      st.ghost.style.transform = `translate(${e.clientX - st.x}px,${e.clientY - st.y}px) scale(.82) rotate(${Math.max(-8, Math.min(8, (e.clientX - st.x) / 40))}deg)`;
      const s = this.sum.getBoundingClientRect();
      this.sum.classList.toggle('is-drop', e.clientX > s.left && e.clientX < s.right && e.clientY > s.top && e.clientY < s.bottom);
    }, { passive: false });
    const end = (e) => {
      if (!st || e.pointerId !== st.id) return;
      const s0 = st;
      if (!s0.on) { st = null; return; }
      const sr = this.sum.getBoundingClientRect();
      const inside = e.type === 'pointerup' && e.clientX > sr.left && e.clientX < sr.right && e.clientY > sr.top && e.clientY < sr.bottom;
      s0.li.classList.remove('is-dragging');
      const g = s0.ghost;
      clear();
      if (inside && this.change(s0.li, 1)) { this.fly(g.src, s0.rect, g); return; }
      if (inside) this.shake();
      // not dropped: glide back to the card
      const cur = g.getBoundingClientRect();
      const a = this.reduced() ? null : g.animate([{ transform: g.style.transform }, { transform: 'translate(0,0) scale(1)' }], { duration: 260, easing: 'ease-out' });
      a ? (a.onfinish = () => g.remove()) : g.remove();
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    this.addEventListener('dragstart', (e) => e.preventDefault());
  }

  trim() { // tier shrank below current selection
    let over = this.count() - this.tier.size;
    for (const [id, it] of [...this.items].reverse()) {
      if (over <= 0) break;
      const cut = Math.min(it.qty, over);
      it.qty -= cut; over -= cut;
      if (!it.qty) this.items.delete(id);
    }
  }

  render() {
    const n = this.count(), size = this.tier.size, pct = this.tier.pct;
    let sub = 0;
    this.items.forEach((i) => (sub += i.qty * i.price));
    const full = n === size;
    const disc = full ? Math.round(sub * pct / 100) : 0;
    const $ = (s) => this.querySelector(s);

    this.querySelectorAll('[data-product]').forEach((li) => {
      const it = this.items.get(li.dataset.id);
      if (!li.querySelector('[data-inc]')) return;
      li.classList.toggle('has-qty', !!it);
      const b = li.querySelector('[data-badge]');
      const prevQ = Number(b.textContent) || 0;
      b.hidden = !it; b.textContent = it ? it.qty : '';
      if (it && it.qty !== prevQ) { b.classList.remove('bump'); void b.offsetWidth; b.classList.add('bump'); }
      li.querySelector('[data-inc]').disabled = n >= size;
    });

    $('[data-count]').textContent = n;
    $('[data-target]').textContent = size;
    $('[data-count-d]').textContent = n;
    $('[data-target-d]').textContent = size;
    $('[data-bar]').style.width = `${(n / size) * 100}%`;
    $('[data-pct]').textContent = `-${pct}%`;
    $('[data-disc-row]').hidden = !pct;
    this.sum.classList.toggle('is-full', full);
    $('[data-sub]').textContent = theme.money(sub);
    $('[data-disc]').textContent = disc ? `-${theme.money(disc)}` : '—';
    $('[data-total]').textContent = theme.money(sub - disc);
    $('[data-total-m]').textContent = theme.money(sub - disc);
    $('[data-hint]').textContent = full ? '' : this.dataset.labelMore.replace('__N__', size - n);
    $('[data-add]').disabled = !full;
    const pay = $('[data-checkout]');
    pay.disabled = !full;
    pay.textContent = full ? this.dataset.labelAdd : this.dataset.labelMore.replace('__N__', size - n);

    const slots = $('[data-slots]');
    slots.innerHTML = '';
    const flat = [];
    this.items.forEach((it) => { for (let k = 0; k < it.qty; k++) flat.push(it); });
    for (let i = 0; i < size; i++) {
      const s = document.createElement('li');
      const fresh = flat[i] && i >= this.prevN && this.prevN < n;
      s.className = 'bb__slot' + (flat[i] ? ' is-filled' : '') + (fresh ? (this.reduced() ? ' is-new' : ' is-landing') : '');
      if (flat[i]) {
        const im = document.createElement('img'); im.src = flat[i].img; im.alt = ''; s.append(im);
        const rm = document.createElement('button'); rm.type = 'button'; rm.className = 'bb__rm'; rm.dataset.remove = flat[i].id;
        rm.setAttribute('aria-label', `Remove ${flat[i].title}`); rm.innerHTML = '&times;'; s.append(rm);
        const cap = document.createElement('span'); cap.className = 'bb__cap'; cap.textContent = flat[i].title; s.append(cap);
      }
      slots.append(s);
    }
    this.prevN = Math.min(n, size);
    clearTimeout(this._lt);
    this._lt = setTimeout(() => this.querySelectorAll('.bb__slot.is-landing').forEach((x) => x.classList.replace('is-landing', 'is-new')), 900);
  }

  // mode: 'cart' -> cart page, 'checkout' -> straight to checkout (discount code is already on the session)
  async add(mode) {
    const btns = this.querySelectorAll('[data-add],[data-checkout]');
    const err = this.querySelector('[data-error]');
    err.hidden = true;
    btns.forEach((b) => (b.disabled = true));
    try {
      const bundleId = `b${this.tier.size}-${Date.now().toString(36)}`;
      const lines = [...this.items].map(([id, it]) => ({ id: Number(id), quantity: it.qty, properties: { _bundle: bundleId, _bundle_size: String(this.tier.size) } }));
      if (this.tier.code) await fetch(`/discount/${encodeURIComponent(this.tier.code)}`, { credentials: 'same-origin' });
      await theme.addToCart(lines);
      location.href = mode === 'checkout' ? '/checkout' : window.themeStrings.cartUrl;
    } catch (e) {
      err.textContent = e.message;
      err.hidden = false;
      btns.forEach((b) => (b.disabled = false));
    }
  }
}
customElements.define('bundle-builder', BundleBuilder);
